import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const TeamMemberCard = ({ member, onUpdateRole, onUpdatePermissions, onRemoveMember }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditingRole, setIsEditingRole] = useState(false);

  const roleColors = {
    'Owner': 'bg-accent/10 text-accent',
    'Designer': 'bg-blue-50 text-blue-700',
    'Sales Rep': 'bg-green-50 text-green-700',
    'Manager': 'bg-purple-50 text-purple-700'
  };

  const statusColors = {
    'Active': 'bg-success/10 text-success',
    'Pending': 'bg-warning/10 text-warning',
    'Inactive': 'bg-error/10 text-error'
  };

  const handlePermissionChange = (permission, value) => {
    const updatedPermissions = {
      ...member?.permissions,
      [permission]: value
    };
    onUpdatePermissions(member?.id, updatedPermissions);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 elevation-1">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
            <Icon name="User" size={20} color="var(--color-secondary-foreground)" />
          </div>
          <div>
            <h3 className="font-medium text-foreground">{member?.name}</h3>
            <p className="text-sm text-muted-foreground">{member?.email}</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
        </Button>
      </div>
      {/* Role and Status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors?.[member?.role] || 'bg-muted text-muted-foreground'}`}>
            {member?.role}
          </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors?.[member?.status] || 'bg-muted text-muted-foreground'}`}>
            {member?.status}
          </span>
        </div>
        <div className="flex items-center space-x-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsEditingRole(true)}
            disabled={member?.role === 'Owner'}
          >
            <Icon name="Edit" size={14} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onRemoveMember(member?.id)}
            disabled={member?.role === 'Owner'}
          >
            <Icon name="Trash2" size={14} />
          </Button>
        </div>
      </div>
      {/* Expanded Permissions */}
      {isExpanded && (
        <div className="border-t border-border pt-3 space-y-3">
          <h4 className="text-sm font-medium text-foreground">Permissions</h4>
          
          <div className="space-y-2">
            <Checkbox
              label="Create Projects"
              checked={member?.permissions?.createProjects}
              onChange={(e) => handlePermissionChange('createProjects', e?.target?.checked)}
              disabled={member?.role === 'Owner'}
            />
            
            <Checkbox
              label="View All Projects"
              checked={member?.permissions?.viewAllProjects}
              onChange={(e) => handlePermissionChange('viewAllProjects', e?.target?.checked)}
              disabled={member?.role === 'Owner'}
            />
            
            <Checkbox
              label="Manage Team"
              checked={member?.permissions?.manageTeam}
              onChange={(e) => handlePermissionChange('manageTeam', e?.target?.checked)}
              disabled={member?.role === 'Owner'}
            />
            
            <Checkbox
              label="Access Vault"
              checked={member?.permissions?.accessVault}
              onChange={(e) => handlePermissionChange('accessVault', e?.target?.checked)}
              disabled={member?.role === 'Owner'}
            />
            
            <Checkbox
              label="Client Communication"
              checked={member?.permissions?.clientCommunication}
              onChange={(e) => handlePermissionChange('clientCommunication', e?.target?.checked)}
              disabled={member?.role === 'Owner'}
            />
          </div>

          <div className="pt-2">
            <p className="text-xs text-muted-foreground">
              Last active: {member?.lastActive}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamMemberCard;